import Vue from "vue";
import Vuex from "vuex";

import { GetPartner } from "@/services/partners/GetPartner";
import { PartnerDetailsType } from "@/types/partner/Partner";
import { validateOnboardingData } from "@/utils/onboardingChecklist";

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    snackbar: { active: false, color: "", message: "", timer: 4000 },
    sessionInfo: {
      id: "",
      name: "",
      nickname: "",
      email: "",
      reRegistration: false,
      balance: 0,
      isPremium: false,
    },
    partnerSession: null as PartnerDetailsType | null,
    sessionLoading: false,
    sessionLoadPromise: null as Promise<PartnerDetailsType> | null,
    onboardingPending: false,
    premiumModalVisible: false,
  },
  getters: {
    isSessionReady(state): boolean {
      return !!state.partnerSession && !state.sessionLoading;
    },
  },
  mutations: {
    UPDATE_SNACKBAR(state, snackbar) {
      state.snackbar = snackbar;
    },
    UPDATE_SESSION_INFO(state, sessionInfo) {
      state.sessionInfo.id = sessionInfo.id || state.sessionInfo.id || "";
      state.sessionInfo.name = sessionInfo.name;
      state.sessionInfo.nickname = sessionInfo.nickname;
      state.sessionInfo.email = sessionInfo.email;
      state.sessionInfo.reRegistration = sessionInfo.reRegistration;
      state.sessionInfo.balance = sessionInfo.balance;
      state.sessionInfo.isPremium = Boolean(sessionInfo.isPremium);
    },
    SET_PARTNER_SESSION(state, partner: PartnerDetailsType | null) {
      state.partnerSession = partner;
    },
    SET_SESSION_LOADING(state, loading: boolean) {
      state.sessionLoading = loading;
    },
    SET_SESSION_LOAD_PROMISE(
      state,
      promise: Promise<PartnerDetailsType> | null,
    ) {
      state.sessionLoadPromise = promise;
    },
    SET_ONBOARDING_PENDING(state, value: boolean) {
      state.onboardingPending = Boolean(value);
    },
    SET_PREMIUM_MODAL_VISIBILITY(state, visible: boolean) {
      state.premiumModalVisible = Boolean(visible);
    },
  },
  actions: {
    showSnack({ commit }, { message, color, timer }) {
      commit("UPDATE_SNACKBAR", {
        active: true,
        color: color,
        message: message,
        timer: timer,
      });
    },
    setSessionInfo({ commit }, { name, nickname, email, reRegistration, balance, isPremium }) {
      commit("UPDATE_SESSION_INFO", {
        name: name,
        nickname: nickname,
        email: email,
        reRegistration: reRegistration,
        balance: balance,
        isPremium: isPremium,
      });
    },
    setOnboardingPending({ commit }, value: boolean) {
      commit("SET_ONBOARDING_PENDING", value);
    },
    setPremiumModalVisibility({ commit }, visible: boolean) {
      commit("SET_PREMIUM_MODAL_VISIBILITY", visible);
    },
    async loadSession({ commit, state }, options?: { force?: boolean }) {
      const force = options?.force === true;

      if (!force && state.partnerSession) {
        return state.partnerSession;
      }

      if (!force && state.sessionLoadPromise) {
        return state.sessionLoadPromise;
      }

      const getPartner = new GetPartner();
      commit("SET_SESSION_LOADING", true);

      const promise = getPartner
        .execute()
        .then((partner) => {
          commit("SET_PARTNER_SESSION", partner);
          commit("UPDATE_SESSION_INFO", {
            id: partner.id || "",
            name: partner.name || "",
            nickname: partner.nickname || partner.nickName || "",
            email: partner.email || "",
            reRegistration: partner.reRegistration || false,
            balance: partner.balance || 0,
            isPremium: Boolean(partner.isPremium),
          });

          const { hasPending } = validateOnboardingData(partner);
          commit("SET_ONBOARDING_PENDING", hasPending);
          return partner;
        })
        .finally(() => {
          commit("SET_SESSION_LOADING", false);
          commit("SET_SESSION_LOAD_PROMISE", null);
        });

      commit("SET_SESSION_LOAD_PROMISE", promise);
      return promise;
    },
    clearSession({ commit }) {
      commit("SET_PARTNER_SESSION", null);
      commit("SET_SESSION_LOADING", false);
      commit("SET_SESSION_LOAD_PROMISE", null);
      commit("UPDATE_SESSION_INFO", {
        id: "",
        name: "",
        nickname: "",
        email: "",
        reRegistration: false,
        balance: 0,
        isPremium: false,
      });
      commit("SET_ONBOARDING_PENDING", false);
    },
  },
  modules: {},
});
